// Conversation context types for Phase 3

export interface ConversationMessage {
  role: "user" | "assistant";
  query?: string;
  results?: any[];
  summary?: string;
  timestamp: Date;
}

export interface ConversationState {
  history: ConversationMessage[];
  currentResults: any[];
  currentQuery: string;
  previousResults?: any[]; // For "go back" functionality
  previousQuery?: string;
}

export interface QueryContext {
  previousQuery?: string;
  previousResults?: any[];
  currentResults?: any[];
  resultCount?: number;
  totalAmount?: number;
}

export const createEmptyConversation = (): ConversationState => ({
  history: [],
  currentResults: [],
  currentQuery: "",
});

export const addUserMessage = (
  state: ConversationState,
  query: string,
): ConversationState => ({
  ...state,
  history: [
    ...state.history,
    {
      role: "user",
      query,
      timestamp: new Date(),
    },
  ],
  currentQuery: query,
  // Preserve currentResults and previousResults for follow-up context
  currentResults: state.currentResults,
  previousResults: state.previousResults,
  previousQuery: state.previousQuery,
});

export const addAssistantMessage = (
  state: ConversationState,
  results: any[],
  summary: string,
): ConversationState => ({
  ...state,
  history: [
    ...state.history,
    {
      role: "assistant",
      results,
      summary,
      timestamp: new Date(),
    },
  ],
  previousResults: state.currentResults,
  previousQuery: state.currentQuery,
  currentResults: results,
});

export const resetConversation = (): ConversationState =>
  createEmptyConversation();

export const getLastUserQuery = (
  state: ConversationState,
): string | undefined => {
  const lastUserMessage = [...state.history]
    .reverse()
    .find((msg) => msg.role === "user");
  return lastUserMessage?.query;
};

export const getLastResults = (state: ConversationState): any[] => {
  return state.currentResults;
};

export const buildContextSummary = (state: ConversationState): string => {
  if (state.history.length === 0) {
    return "No previous context.";
  }

  const lastUserMessage = [...state.history]
    .reverse()
    .find((msg) => msg.role === "user");

  const lastAssistantMessage = [...state.history]
    .reverse()
    .find((msg) => msg.role === "assistant");

  if (!lastUserMessage || !lastAssistantMessage) {
    return "No previous context.";
  }

  const results = lastAssistantMessage.results || [];
  const summary = lastAssistantMessage.summary || "";

  return `Previous query: "${lastUserMessage.query}"
Results: ${results.length} items
Summary: ${summary}`;
};
